import { useState, useEffect, useRef } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Mail, Lock, User, ArrowLeft, CheckCircle, Gift, Eye, EyeOff } from "lucide-react";
import { useTranslation } from "@/lib/i18n/useTranslation";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { z } from "zod";
import { PasswordStrengthIndicator } from "@/components/auth/PasswordStrengthIndicator";
import { ForgotPasswordModal } from "@/components/auth/ForgotPasswordModal";
import { SEO } from "@/components/seo/SEO";
import { useAuth } from "@/contexts/AuthContext";
import easyfinderLogo from "@/assets/easyfinder-logo-google.png";
import { isDespia } from "@/lib/isDespia";

const Auth = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { status, profile, profileLoaded, isHydrated } = useAuth();
  
  // Prevent multiple redirects
  const didRedirect = useRef(false);

  // Dynamic Zod schemas with translations
  const emailSchema = z
    .string()
    .trim()
    .min(1, t.auth.validation.emailRequired)
    .email(t.auth.validation.emailInvalid)
    .max(255, t.auth.validation.emailTooLong)
    .refine(
      (email) => !email.includes("+") || email.split("+").length <= 2,
      t.auth.validation.emailFormat
    );

  // Critères simplifiés : 8 caractères, 1 lettre, 1 chiffre
  const passwordSchema = z
    .string()
    .min(8, t.auth.validation.passwordMin)
    .max(128, t.auth.validation.passwordMax)
    .regex(/[a-zA-Z]/, "Le mot de passe doit contenir au moins une lettre")
    .regex(/[0-9]/, t.auth.validation.passwordDigit);

  const fullNameSchema = z
    .string()
    .trim()
    .min(2, t.auth.validation.nameMin)
    .max(100, t.auth.validation.nameMax)
    .regex(/^[a-zA-ZÀ-ÿ\s'-]+$/, t.auth.validation.nameFormat);

  const signInSchema = z.object({
    email: emailSchema,
    password: z.string().min(1, t.auth.validation.passwordRequired),
  });

  const signUpSchema = z.object({
    email: emailSchema,
    password: passwordSchema,
    fullName: fullNameSchema,
  });

  // Get referral code from URL if present
  const referralCodeFromUrl = searchParams.get("ref");
  
  const [isSignUp, setIsSignUp] = useState(searchParams.get("mode") === "signup" || !!referralCodeFromUrl);
  const [loading, setLoading] = useState(false);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [fullName, setFullName] = useState("");
  const [referralCode, setReferralCode] = useState(referralCodeFromUrl || "");
  const [errors, setErrors] = useState<{ email?: string; password?: string; fullName?: string }>({});
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [emailSent, setEmailSent] = useState(false);
  const [registeredEmail, setRegisteredEmail] = useState("");
  const [showReferralField, setShowReferralField] = useState(!!referralCodeFromUrl);
  const [showPassword, setShowPassword] = useState(false);

  // Handle OAuth tokens from deeplink (Easy OAuth for Despia)
  useEffect(() => {
    const handleDeeplinkTokens = async () => {
      const accessToken = searchParams.get('access_token');
      const refreshToken = searchParams.get('refresh_token');
      const error = searchParams.get('error');
      const errorDescription = searchParams.get('error_description');

      if (error) {
        console.error('[Auth] OAuth error from deeplink:', error, errorDescription);
        toast.error(errorDescription || 'Erreur de connexion');
        return;
      }

      if (accessToken && refreshToken) {
        console.log('[Auth] Setting session from deeplink tokens');
        setLoading(true);
        try {
          const { error: sessionError } = await supabase.auth.setSession({
            access_token: accessToken,
            refresh_token: refreshToken,
          });
          if (sessionError) throw sessionError;
          toast.success(t.auth.welcomeBackToast);
          // AuthContext will handle redirect after session is set
        } catch (err: unknown) {
          const message = err instanceof Error ? err.message : 'Erreur inconnue';
          console.error('[Auth] Failed to set session:', message);
          toast.error('Erreur lors de la connexion: ' + message);
        } finally {
          setLoading(false);
        }
      }
    };

    handleDeeplinkTokens();
  }, [searchParams, t]);

  // Single redirect effect based on AuthContext (no direct Supabase calls)
  useEffect(() => {
    // Wait for auth to be fully hydrated and profile loaded
    if (!isHydrated || status === "loading" || status === "hydrating") return;
    
    // Only redirect if authenticated
    if (status === "authenticated" && profileLoaded && !didRedirect.current) {
      didRedirect.current = true;
      
      if (profile?.onboarding_completed) {
        navigate("/documents", { replace: true });
      } else {
        navigate("/onboarding", { replace: true });
      }
    }
  }, [status, isHydrated, profileLoaded, profile, navigate]);

  const validateForm = (): boolean => {
    setErrors({});
    
    try {
      if (isSignUp) {
        signUpSchema.parse({ email, password, fullName });
      } else {
        signInSchema.parse({ email, password });
      }
      return true;
    } catch (error) {
      if (error instanceof z.ZodError) {
        const newErrors: typeof errors = {};
        error.issues.forEach((issue) => {
          const field = issue.path[0] as keyof typeof errors;
          if (field && !newErrors[field]) {
            newErrors[field] = issue.message;
          }
        });
        setErrors(newErrors);
      }
      return false;
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }
    
    setLoading(true);

    try {
      if (isSignUp) {
        const redirectUrl = `${window.location.origin}/`;

        const { data, error } = await supabase.auth.signUp({
          email: email.trim(),
          password,
          options: {
            data: {
              full_name: fullName.trim(),
              referral_code: referralCode.trim().toUpperCase() || null,
            },
            emailRedirectTo: redirectUrl,
          },
        });
        if (error) throw error;
        
        // Apply referral code if provided and user was created
        if (referralCode.trim() && data.user) {
          try {
            const { data: applied } = await supabase.rpc("apply_referral_code", {
              p_referred_id: data.user.id,
              p_code: referralCode.trim().toUpperCase(),
            });
            if (applied) {
              toast.success("Code de parrainage appliqué !");
            }
          } catch (refErr) {
            // Silently fail - referral is bonus, not critical
          }
        }
        
        setRegisteredEmail(email.trim());
        setEmailSent(true);
      } else {
        const { error } = await supabase.auth.signInWithPassword({ 
          email: email.trim(), 
          password 
        });
        if (error) throw error;
        toast.success(t.auth.welcomeBackToast);
      }
    } catch (error: any) {
      const rawMessage = typeof error?.message === "string" ? error.message : "";
      const message = rawMessage.includes("Invalid login")
        ? t.auth.invalidCredentials
        : rawMessage.includes("already registered")
        ? t.auth.emailInUse
        : rawMessage.includes("Email not confirmed")
        ? t.auth.confirmEmailFirst
        : rawMessage.includes("weak") || rawMessage.includes("pwned") || rawMessage.includes("easy to guess")
        ? "Ce mot de passe a été détecté dans une fuite de données. Choisissez un mot de passe plus unique (évitez les mots du dictionnaire et les suites simples)."
        : rawMessage || t.auth.genericError;
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  // Helper to get OAuth redirect URL (handles native Capacitor + web)
  const getOAuthRedirectUrl = () => {
    const origin = window.location.origin;

    // Check if running in native Capacitor app
    const isNative =
      typeof (window as any)?.Capacitor !== "undefined" &&
      (window as any)?.Capacitor?.isNativePlatform?.();

    // Native app: redirect to a dedicated web callback path.
    // IMPORTANT: we use a *path* (not a query param) because some OAuth providers / clients
    // can drop custom query params from redirectTo.
    // The web callback will then deep-link back into the native app.
    if (isNative) return "https://easyfinder.fr/auth/callback/native";

    // Web: always redirect back to the same origin (preview stays in preview, prod stays in prod)
    return `${origin}/auth/callback`;
  };

  const handleGoogleSignIn = async () => {
    // Check if we should force account selection (from Settings "Change account")
    const forceSelectAccount = searchParams.get("select_account") === "true";
    
    // Custom Google OAuth flow for Despia native apps - bypasses Supabase Auth
    if (isDespia()) {
      window.__DEBUG_LOG__?.('🔵 Start Google Flow (Despia)');
      console.log('[Auth] Starting Custom Google Flow for Despia');
      setLoading(true);
      
      try {
        // Ask backend for the Google OAuth URL (keeps client_id in one place)
        const { data, error: fnError } = await supabase.functions.invoke('google-auth-start', {
          body: {
            force_select_account: forceSelectAccount,
          },
        });

        if (fnError) throw fnError;
        if (!data?.url) throw new Error('No Google OAuth URL returned');

        const googleUrl: string = data.url;

        window.__DEBUG_LOG__?.(`🌐 Google URL: ${googleUrl.substring(0, 80)}...`);
        console.log('[Auth] Opening secure browser with direct Google OAuth URL');
        
        // Open secure browser via Despia
        // The "oauth://" scheme tells Despia to use ASWebAuthenticationSession (iOS)
        const despiaOAuthUrl = `oauth://?url=${encodeURIComponent(googleUrl)}`;
        
        window.__DEBUG_LOG__?.('📱 Opening secure browser...');
        
        // Import and use despia-native SDK
        try {
          const { default: despia } = await import('despia-native');
          window.__DEBUG_LOG__?.('📦 despia-native imported');
          await despia(despiaOAuthUrl);
          window.__DEBUG_LOG__?.('✅ despia() called');
        } catch (importError) {
          window.__DEBUG_LOG__?.(`❌ despia import failed: ${importError}`);
          window.location.href = despiaOAuthUrl;
        }
      } catch (err: unknown) {
        const message = err instanceof Error ? err.message : 'Erreur inconnue';
        window.__DEBUG_LOG__?.(`❌ Google Error: ${message}`);
        console.error('[Auth] Custom Google OAuth error:', message);
        toast.error('Erreur de connexion Google: ' + message);
        setLoading(false);
      }
      return;
    }

    // Standard OAuth flow for web
    const { error } = await supabase.auth.signInWithOAuth({
      provider: "google",
      options: { 
        redirectTo: getOAuthRedirectUrl(),
        queryParams: forceSelectAccount ? { prompt: "select_account" } : undefined,
      },
    });
    if (error) toast.error(t.auth.googleNotConfigured);
  };

  const handleAppleSignIn = async () => {
    // Custom Apple OAuth flow for Despia native apps - bypasses Supabase Auth
    if (isDespia()) {
      window.__DEBUG_LOG__?.('🍎 Start Apple Flow (Despia)');
      console.log('[Auth] Starting Custom Apple Flow for Despia');
      setLoading(true);
      
      try {
        // Ask backend for the Apple OAuth URL
        const { data, error: fnError } = await supabase.functions.invoke('apple-auth-start', {
          body: { is_native: true },
        });

        if (fnError) throw fnError;
        if (!data?.url) throw new Error('No Apple OAuth URL returned');

        const appleUrl: string = data.url;
        
        window.__DEBUG_LOG__?.(`🌐 Apple URL: ${appleUrl.substring(0, 80)}...`);
        console.log('[Auth] Opening secure browser with Apple OAuth URL');
        
        // Open secure browser via Despia
        const despiaOAuthUrl = `oauth://?url=${encodeURIComponent(appleUrl)}`;
        
        window.__DEBUG_LOG__?.('📱 Opening secure browser...');
        
        try {
          const { default: despia } = await import('despia-native');
          window.__DEBUG_LOG__?.('📦 despia-native imported');
          await despia(despiaOAuthUrl);
          window.__DEBUG_LOG__?.('✅ despia() called');
        } catch (importError) {
          window.__DEBUG_LOG__?.(`❌ despia import failed: ${importError}`);
          window.location.href = despiaOAuthUrl;
        }
      } catch (err: unknown) {
        const message = err instanceof Error ? err.message : 'Erreur inconnue';
        window.__DEBUG_LOG__?.(`❌ Apple Error: ${message}`);
        console.error('[Auth] Custom Apple OAuth error:', message);
        toast.error('Erreur de connexion Apple: ' + message);
        setLoading(false);
      }
      return;
    }

    // Web flow using Apple JS SDK
    setLoading(true);
    try {
      // Web popup: Apple issues the code for this web callback URL
      const webRedirectUri = `${window.location.origin}/callback/apple`;
      
      // Initialize Apple Sign In
      const AppleID = (window as any).AppleID;
      if (!AppleID?.auth) {
        throw new Error('Apple Sign In SDK not loaded');
      }

      AppleID.auth.init({
        clientId: 'fr.easyfinder.app.web',
        scope: 'name email',
        redirectURI: webRedirectUri,
        usePopup: true,
      });

      // Trigger Apple Sign In popup
      const response = await AppleID.auth.signIn();
      
      console.log('[Auth] Apple Sign In response received');
      
      // Extract code and id_token from response
      const { authorization } = response;
      if (!authorization?.code || !authorization?.id_token) {
        throw new Error('Missing authorization data from Apple');
      }

      // Call our edge function to exchange the code
      // IMPORTANT: Pass the same redirect_uri that was used for authorization
      const { data, error: fnError } = await supabase.functions.invoke('apple-auth-callback', {
        body: { 
          code: authorization.code, 
          id_token: authorization.id_token,
          redirect_uri: webRedirectUri,
        },
      });

      if (fnError) throw fnError;
      if (!data?.access_token || !data?.refresh_token) {
        throw new Error('No tokens received from server');
      }

      // Set the session
      const { error: sessionError } = await supabase.auth.setSession({
        access_token: data.access_token,
        refresh_token: data.refresh_token,
      });

      if (sessionError) throw sessionError;
      
      toast.success(t.auth.welcomeBackToast);
      // AuthContext will handle redirect
      
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Erreur inconnue';
      // User cancelled is not an error
      if (message.includes('popup_closed') || message.includes('user_cancelled')) {
        console.log('[Auth] User cancelled Apple Sign In');
      } else {
        console.error('[Auth] Apple Sign In error:', message);
        toast.error('Erreur de connexion Apple: ' + message);
      }
    } finally {
      setLoading(false);
    }
  };


  // Email verification confirmation screen
  if (emailSent) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-4">
        <div className="w-full max-w-md">
          <div className="flex items-center justify-center gap-2 mb-8">
            <img src={easyfinderLogo} alt="EasyFinder" className="w-10 h-10 rounded-xl shadow-lg shadow-primary/25" />
            <span className="text-2xl font-bold">EasyFinder</span>
          </div>

          <Card>
            <CardContent className="p-8 text-center">
              <div className="w-16 h-16 rounded-full bg-primary/10 flex items-center justify-center mx-auto mb-6">
                <CheckCircle className="w-8 h-8 text-primary" />
              </div>
              <h2 className="text-xl font-bold mb-2">{t.auth.checkEmail}</h2>
              <p className="text-muted-foreground mb-6">
                {t.auth.confirmationSent}{" "}
                <span className="font-medium text-foreground">{registeredEmail}</span>
              </p>
              <p className="text-sm text-muted-foreground mb-6">
                {t.auth.clickToActivate}
              </p>
              <div className="space-y-3">
                <Button
                  variant="outline"
                  className="w-full"
                  onClick={() => {
                    setEmailSent(false);
                    setIsSignUp(false);
                    setPassword("");
                  }}
                >
                  <Mail className="w-4 h-4 mr-2" />
                  {t.auth.confirmedEmail}
                </Button>
                <p className="text-xs text-muted-foreground">
                  {t.auth.noEmailReceived}{" "}
                  <button
                    type="button"
                    className="text-primary hover:underline"
                    onClick={async () => {
                      const { error } = await supabase.auth.resend({
                        type: "signup",
                        email: registeredEmail,
                        options: {
                          emailRedirectTo: `${window.location.origin}/`,
                        },
                      });
                      if (error) {
                        toast.error(t.auth.resendError);
                      } else {
                        toast.success(t.auth.resendSuccess);
                      }
                    }}
                  >
                    {t.auth.resend}
                  </button>
                </p>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background flex items-center justify-center p-4">
      <SEO 
        title={isSignUp ? "Créer un compte" : "Connexion"}
        description="Connectez-vous à EasyFinder pour gérer vos documents. Inscription gratuite, accès instantané à votre espace sécurisé."
        canonical="/auth"
      />
      <div className="absolute top-4 left-4">
        <Button variant="ghost" onClick={() => navigate("/")}>
          <ArrowLeft className="w-4 h-4 mr-2" />
          {t.common.back}
        </Button>
      </div>

      <div className="w-full max-w-md">
        <div className="flex items-center justify-center gap-2 mb-8">
          <img src={easyfinderLogo} alt="EasyFinder" className="w-10 h-10 rounded-xl shadow-lg shadow-primary/25" />
          <span className="text-2xl font-bold">EasyFinder</span>
        </div>

        <Card>
          <CardHeader className="text-center pb-4">
            <CardTitle>{isSignUp ? t.auth.createAccount : t.auth.welcomeBack}</CardTitle>
            <CardDescription>
              {isSignUp ? t.auth.startOrganizing : t.auth.signInAccess}
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-4">
              {isSignUp && (
                <div className="space-y-2">
                  <Label htmlFor="name">{t.auth.fullName}</Label>
                  <div className="relative">
                    <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                    <Input
                      id="name"
                      placeholder="Jean Dupont"
                      value={fullName}
                      onChange={(e) => {
                        setFullName(e.target.value);
                        if (errors.fullName) setErrors((prev) => ({ ...prev, fullName: undefined }));
                      }}
                      className={`pl-10 ${errors.fullName ? "border-destructive" : ""}`}
                    />
                  </div>
                  {errors.fullName && (
                    <p className="text-xs text-destructive">{errors.fullName}</p>
                  )}
                </div>
              )}

              <div className="space-y-2">
                <Label htmlFor="email">{t.auth.email}</Label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                  <Input
                    id="email"
                    type="email"
                    placeholder="vous@exemple.com"
                    value={email}
                    onChange={(e) => {
                      setEmail(e.target.value);
                      if (errors.email) setErrors((prev) => ({ ...prev, email: undefined }));
                    }}
                    className={`pl-10 ${errors.email ? "border-destructive" : ""}`}
                  />
                </div>
                {errors.email && (
                  <p className="text-xs text-destructive">{errors.email}</p>
                )}
              </div>

              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <Label htmlFor="password">{t.auth.password}</Label>
                  {!isSignUp && (
                    <button
                      type="button"
                      className="text-xs text-primary hover:underline"
                      onClick={() => setShowForgotPassword(true)}
                    >
                      {t.auth.forgotPassword}
                    </button>
                  )}
                </div>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                  <Input
                    id="password"
                    type={showPassword ? "text" : "password"}
                    placeholder="••••••••"
                    value={password}
                    onChange={(e) => {
                      setPassword(e.target.value);
                      if (errors.password) setErrors((prev) => ({ ...prev, password: undefined }));
                    }}
                    className={`pl-10 pr-10 ${errors.password ? "border-destructive" : ""}`}
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
                    tabIndex={-1}
                  >
                    {showPassword ? (
                      <EyeOff className="w-4 h-4" />
                    ) : (
                      <Eye className="w-4 h-4" />
                    )}
                  </button>
                </div>
                {errors.password && (
                  <p className="text-xs text-destructive">{errors.password}</p>
                )}
                {isSignUp && <PasswordStrengthIndicator password={password} />}
              </div>

              <Button type="submit" className="w-full h-11" disabled={loading}>
                {loading ? t.common.loading : isSignUp ? t.auth.createAccountBtn : t.auth.signInBtn}
              </Button>

              {/* Phrase de réassurance - uniquement sur inscription */}
              {isSignUp && (
                <div className="flex items-center justify-center gap-4 text-xs text-muted-foreground/80 py-2">
                  <span className="flex items-center gap-1">
                    <CheckCircle className="w-3.5 h-3.5 text-emerald-500" />
                    Aucune carte requise
                  </span>
                  <span className="flex items-center gap-1">
                    <CheckCircle className="w-3.5 h-3.5 text-emerald-500" />
                    Annulable à tout moment
                  </span>
                </div>
              )}

              {/* Code de parrainage - caché par défaut */}
              {isSignUp && !showReferralField && (
                <button
                  type="button"
                  onClick={() => setShowReferralField(true)}
                  className="w-full text-center text-xs text-primary hover:underline flex items-center justify-center gap-1.5"
                >
                  <Gift className="w-3.5 h-3.5" />
                  J'ai un code de parrainage
                </button>
              )}

              {isSignUp && showReferralField && (
                <div className="space-y-2 pt-2 border-t border-border/50">
                  <Label htmlFor="referral" className="flex items-center gap-2 text-sm">
                    <Gift className="w-4 h-4 text-primary" />
                    Code de parrainage
                  </Label>
                  <Input
                    id="referral"
                    placeholder="ABCD1234"
                    value={referralCode}
                    onChange={(e) => setReferralCode(e.target.value.toUpperCase())}
                    className="font-mono tracking-wider"
                    maxLength={8}
                  />
                  {referralCode && (
                    <p className="text-xs text-emerald-600">
                      🎉 -20% sur votre premier abonnement !
                    </p>
                  )}
                </div>
              )}
            </form>

            {/* Social login buttons */}
            <div className="mt-6 space-y-3">
              <div className="relative">
                <div className="absolute inset-0 flex items-center">
                  <span className="w-full border-t" />
                </div>
                <div className="relative flex justify-center text-xs uppercase">
                  <span className="bg-background px-2 text-muted-foreground">
                    {t.auth.orContinueWith}
                  </span>
                </div>
              </div>

              <Button
                type="button"
                variant="outline"
                className="w-full"
                onClick={handleGoogleSignIn}
                disabled={loading}
              >
                <svg className="w-4 h-4 mr-2" viewBox="0 0 24 24">
                  <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                  <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                  <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                  <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                </svg>
                {t.auth.continueWithGoogle}
              </Button>

              <Button
                type="button"
                variant="outline"
                className="w-full"
                onClick={handleAppleSignIn}
                disabled={loading}
              >
                <svg className="w-4 h-4 mr-2" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M17.05 20.28c-.98.95-2.05.8-3.08.35-1.09-.46-2.09-.48-3.24 0-1.44.62-2.2.44-3.06-.35C2.79 15.25 3.51 7.59 9.05 7.31c1.35.07 2.29.74 3.08.8 1.18-.24 2.31-.93 3.57-.84 1.51.12 2.65.72 3.4 1.8-3.12 1.87-2.38 5.98.48 7.13-.57 1.5-1.31 2.99-2.54 4.09l.01-.01zM12.03 7.25c-.15-2.23 1.66-4.07 3.74-4.25.29 2.58-2.34 4.5-3.74 4.25z"/>
                </svg>
                {t.auth.continueWithApple}
              </Button>
            </div>

            <p className="text-center text-sm text-muted-foreground mt-6">
              {isSignUp ? t.auth.alreadyHaveAccount : t.auth.noAccount}{" "}
              <button
                type="button"
                className="text-primary hover:underline font-medium"
                onClick={() => setIsSignUp(!isSignUp)}
              >
                {isSignUp ? t.common.signIn : t.common.signUp}
              </button>
            </p>
          </CardContent>
        </Card>
      </div>

      <ForgotPasswordModal 
        open={showForgotPassword} 
        onOpenChange={setShowForgotPassword} 
      />
    </div>
  );
};

export default Auth;